import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import CustomSwitch from "./Switch";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import partnermarkernew from "../assets/partnermarkernew.svg";
import centermarkernew from "../assets/centermarkernew.svg";
import search from "../assets/search.svg";
import Modal from "./Modal";
import { collection, query, where, getDocs, and, or } from "firebase/firestore";
import { useRecoveryTracker } from "../context/RecoveryTrackerContext";

const RecoveryTrackerOptions = ({ isOpen, onClose }) => {
  const selectedUserId = sessionStorage.getItem("userId");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [localAccess, setLocalAccess] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { accessOptions, fetchAccessData } = useRecoveryTracker();

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();

  // Initialize form with current access data
  useEffect(() => {
    const fetchCurrentAccess = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        if (userDoc.exists()) {
          const currentAccess = userDoc.data()?.has_access_to_rt || [];
          setLocalAccess(currentAccess);

          // Set initial form values
          accessOptions.forEach((opt) => {
            setValue(opt.id, currentAccess.includes(opt.id));
          });

          // Only set selectAll to true if all options are actually selected
          const allSelected =
            accessOptions.length > 0 &&
            accessOptions.every((option) => currentAccess.includes(option.id));
          setSelectAll(allSelected);
        }
      } catch (error) {
        console.error("Error fetching current access:", error);
      }
    };

    if (isOpen) {
      fetchCurrentAccess();
    } else {
      // Reset state when modal closes
      setSelectAll(false);
      setLocalAccess([]);
    }
  }, [isOpen, selectedUserId, accessOptions, setValue]);

  const handleToggleAccess = useCallback(
    (optionId, event) => {
      // Get current state from localAccess
      const isCurrentlyChecked = localAccess.includes(optionId);

      // Toggle to opposite state
      const newCheckedState = !isCurrentlyChecked;

      // Update form value
      setValue(optionId, newCheckedState);

      // Update localAccess
      setLocalAccess((prev) => {
        if (newCheckedState) {
          return [...prev, optionId];
        } else {
          return prev.filter((id) => id !== optionId);
        }
      });

      // Update selectAll state
      const allSelected = accessOptions.every((opt) =>
        opt.id === optionId ? newCheckedState : localAccess.includes(opt.id)
      );
      setSelectAll(allSelected);
    },
    [localAccess, accessOptions, setValue]
  );

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);

    // Update all form values
    accessOptions.forEach((opt) => {
      setValue(opt.id, isChecked);
    });

    // Update localAccess
    setLocalAccess(isChecked ? accessOptions.map((opt) => opt.id) : []);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Get current form values
      const currentFormValues = getValues();

      // Create array of selected IDs
      const selectedAccess = accessOptions
        .filter((opt) => currentFormValues[opt.id])
        .map((opt) => opt.id);

      const userRef = doc(db, "users", selectedUserId);
      await updateDoc(userRef, {
        has_access_to_rt: selectedAccess,
      });

      await fetchAccessData();
      onClose();
    } catch (error) {
      console.error("Error saving access:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOptions = useMemo(
    () =>
      accessOptions.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [accessOptions, searchQuery]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Give Access To"
      width="800px"
      height="600px"
      noFooter={false}
      customLeftButtonText="Cancel"
      customRightButtonText="Save"
      customOnRightButtonClick={handleSave}
      customOnLeftButtonClick={onClose}
      isLoading={isLoading}
      isEdit={true}
    >
      <div className="flex h-full flex-col items-start">
        <div className="mb-4 w-full">
          <div className="relative w-full">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 transform">
              <img
                src={search}
                alt="Search"
                className="m-0 my-auto h-5 w-5 p-0"
              />
            </div>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-12 pr-3 text-base focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="mb-4 w-full border-b border-gray-200">
          <div className="flex w-full items-center gap-2">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="peer h-6 w-6 cursor-pointer appearance-none rounded-[2px] border-2 border-[#003460] bg-white shadow-[inset_0_0_0_1px_#003460] checked:border-[#003460] checked:bg-[#003460] checked:shadow-none"
              />
              <svg
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3334 4L6.00008 11.3333L2.66675 8"
                  stroke="#F7CA41"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text=[#242424] text-base font-medium">
              Give access to all
            </span>
          </div>
        </div>

        <div className="w-full flex-1 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={`${option.id}-${index}`}
              className="hover:bg-gray-100 flex items-center justify-between py-2"
            >
              <div className="flex items-center gap-2">
                <img
                  src={
                    option.type === "partner"
                      ? partnermarkernew
                      : centermarkernew
                  }
                  alt={
                    option.type === "partner" ? "Partner icon" : "Center icon"
                  }
                  className="h-8 w-8"
                />
                <div className="flex flex-col justify-between gap-1">
                  <span className="text=[#242424] text-base font-medium">
                    {option.name}
                  </span>
                  <span className="text-base font-normal text-[#828282]">
                    {option.type === "partner" ? "Partner" : "Center"}
                  </span>
                </div>
              </div>
              <CustomSwitch
                control={control}
                name={option.id}
                rowType={true}
                onColor="#0A8525"
                width={42}
                height={24}
                handleDiameter={16}
                errors={errors}
                rules={{
                  onChange: (e) => handleToggleAccess(option.id, e),
                }}
                className="-ml-2"
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

RecoveryTrackerOptions.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RecoveryTrackerOptions;
